import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { I } from './Icons'
import WipAiMatterSelect from './WipAiMatterSelect'
import WipAiMonthSelect from './WipAiMonthSelect'
import { WIP_AI_PENDING_REV_ID } from '../store/useWipAiStore'

const Topbar = styled.header`
  position: sticky;
  top: 0;
  z-index: 50;
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 20px;
  background: ${p => p.theme.colors.semantic.surface};
  border-bottom: 1px solid ${p => p.theme.colors.semantic.border};
`

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  font-size: 13px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${p => p.theme.colors.semantic.textPrimary};
`

const BrandMark = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: ${p => p.theme.colors.black};
  display: grid;
  place-items: center;
  color: #fff;
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  font-weight: 700;
  font-size: 11px;
`

const Crumbs = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  font-size: 13px;
  color: ${p => p.theme.colors.semantic.textMuted};
`

const CrumbSep = styled.span`
  color: ${p => p.theme.colors.semantic.borderStrong};
`

const Grow = styled.div`
  flex: 1;
`

const PillBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 34px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid ${p => p.theme.colors.semantic.border};
  background: ${p => p.theme.colors.semantic.surface};
  color: ${p => p.theme.colors.semantic.textPrimary};
  font-size: 13px;
  font-weight: 500;
  &:hover {
    background: ${p => p.theme.colors.semantic.surfaceMuted};
  }
  svg {
    width: 14px;
    height: 14px;
    opacity: 0.6;
  }
`

const PillBtnPrimary = styled(PillBtn)`
  background: ${p => p.theme.colors.semantic.accent};
  border-color: ${p => p.theme.colors.semantic.accent};
  color: #fff;
  svg {
    opacity: 1;
  }
  &:hover {
    background: ${p => p.theme.colors.semantic.accentHover};
    border-color: ${p => p.theme.colors.semantic.accentHover};
  }
`

const RevChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 34px;
  padding: 0 6px 0 12px;
  border-radius: 8px;
  border: 1px solid ${p => p.theme.colors.semantic.border};
  background: ${p => p.theme.colors.semantic.surface};
  font-size: 13px;
  font-weight: 500;
`

const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${p => p.$bg};
`

const Meta = styled.span`
  color: ${p => p.theme.colors.semantic.textMuted};
  font-size: 12px;
`

const Latest = styled.span`
  padding: 2px 7px;
  border-radius: 4px;
  background: ${p => p.theme.colors.semantic.lilacTint};
  color: ${p => p.theme.colors.semantic.accent};
  font-size: 11px;
  font-weight: 600;
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
`

const CaretBtn = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 4px;
  display: grid;
  place-items: center;
  color: ${p => p.theme.colors.semantic.textMuted};
  &:hover {
    background: ${p => p.theme.colors.semantic.surfaceMuted};
  }
`

const WipAiTopBar = ({
  projectName,
  availableReportMonths,
  selectedReportMonth,
  onSelectReportMonth,
  monthSelectDisabled,
  projects,
  selectedProject,
  onSelectProject,
  activeRev,
  latestRev,
  onPreview,
  onExportDocx,
  onTweaks,
}) => {
  const src = activeRev?.source
  const dotBg = src === 'ai' ? '#050519' : src === 'user' ? '#5856FF' : '#4241BF'

  return (
    <Topbar>
      <Brand>
        <BrandMark>K</BrandMark>
        <span>KEYSTONE</span>
      </Brand>
      <Crumbs>
        <CrumbSep>/</CrumbSep>
        <span>Matters</span>
        <CrumbSep>/</CrumbSep>
        <strong style={{ color: '#050519', fontWeight: 600 }}>{projectName}</strong>
        <CrumbSep>/</CrumbSep>
        <span>WIP</span>
      </Crumbs>
      <Grow />
      <WipAiMatterSelect
        projects={projects}
        selectedProject={selectedProject}
        onSelectProject={onSelectProject}
      />
      <WipAiMonthSelect
        months={availableReportMonths}
        selectedMonth={selectedReportMonth}
        onSelectMonth={onSelectReportMonth}
        disabled={monthSelectDisabled}
      />
      <RevChip>
        <Dot $bg={dotBg} />
        <span>Rev {activeRev.revisionNumber}</span>
        <Meta>{activeRev.timeLabel}</Meta>
        {activeRev.id === latestRev.id && activeRev.id !== WIP_AI_PENDING_REV_ID && (
          <Latest>LATEST</Latest>
        )}
        <CaretBtn>
          <I.chev width={12} height={12} />
        </CaretBtn>
      </RevChip>
      <PillBtn type="button" onClick={onPreview}>
        <I.eye /> Preview
      </PillBtn>
      <PillBtnPrimary type="button" onClick={onExportDocx}>
        <I.dl /> Export .docx
      </PillBtnPrimary>
      <PillBtn
        type="button"
        onClick={onTweaks}
        style={{ width: 38, padding: 0, justifyContent: 'center' }}
        title="Tweaks"
      >
        <I.settings />
      </PillBtn>
    </Topbar>
  )
}

WipAiTopBar.propTypes = {
  projectName: PropTypes.string.isRequired,
  availableReportMonths: PropTypes.arrayOf(PropTypes.string),
  selectedReportMonth: PropTypes.string,
  onSelectReportMonth: PropTypes.func.isRequired,
  monthSelectDisabled: PropTypes.bool,
  projects: PropTypes.arrayOf(PropTypes.object),
  selectedProject: PropTypes.object,
  onSelectProject: PropTypes.func.isRequired,
  activeRev: PropTypes.object.isRequired,
  latestRev: PropTypes.object.isRequired,
  onPreview: PropTypes.func.isRequired,
  onExportDocx: PropTypes.func.isRequired,
  onTweaks: PropTypes.func.isRequired,
}

export default WipAiTopBar
